import { useState, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

interface AnimatedTextProps {
  text: string
  animate: boolean
  speed?: number // ms per word (default: 60)
  className?: string
}

/**
 * Word-by-word text animation with blur + slide-up effect.
 *
 * Key behaviors:
 * - When `animate=true` and text grows, new words fade in one at a time
 * - Shows a blinking cursor while streaming is active
 * - When `animate=false`, shows full text immediately (historical messages)
 * - Handles text updates mid-animation (summary grows via SSE)
 */
export function AnimatedText({ text, animate, speed = 60, className }: AnimatedTextProps) {
  const words = text ? text.split(/(\s+)/) : []
  const [visibleCount, setVisibleCount] = useState(animate ? 0 : words.length)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const prevWordCountRef = useRef(words.length)

  // Clear interval helper
  const clearTimer = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  // Animate words incrementally
  useEffect(() => {
    if (!animate) {
      setVisibleCount(words.length)
      clearTimer()
      return
    }

    // Text grew (SSE update) — keep animating from current position
    if (words.length > prevWordCountRef.current) {
      prevWordCountRef.current = words.length
    }

    // Nothing to animate — caught up
    if (visibleCount >= words.length) {
      clearTimer()
      return
    }

    // Start interval if not already running
    if (intervalRef.current === null) {
      intervalRef.current = setInterval(() => {
        setVisibleCount((prev) => {
          const next = prev + 1
          if (next >= words.length) {
            clearTimer()
          }
          return next
        })
      }, speed)
    }

    return clearTimer
  }, [text, words.length, animate, speed, visibleCount])

  // When animation stops externally, reveal everything
  useEffect(() => {
    if (!animate) {
      setVisibleCount(words.length)
      clearTimer()
    }
  }, [animate, words.length])

  // Update visible count reference when words change
  useEffect(() => {
    prevWordCountRef.current = words.length
  }, [words.length])

  const isAnimating = animate && visibleCount < words.length
  const showCursor = animate

  return (
    <span className={cn('text-foreground text-sm leading-relaxed', className)}>
      {words.slice(0, visibleCount).map((word, i) => {
        // Whitespace tokens render as-is (no animation)
        if (/^\s+$/.test(word)) {
          return <span key={`ws-${i}`}>{word}</span>
        }
        return (
          <span
            key={`${i}-${word}`}
            className="inline-block animate-word-pop"
          >
            {word}
          </span>
        )
      })}
      {showCursor && (
        <span
          className={cn(
            'inline-block w-[2px] h-[1em] ml-[1px] align-text-bottom rounded-sm',
            isAnimating
              ? 'bg-primary/80'
              : 'streaming-cursor bg-primary/60'
          )}
        />
      )}
    </span>
  )
}
